import { useEffect, useRef, useState } from 'react';
import { ChevronDownIcon } from '@radix-ui/react-icons';
import { ThemedInput } from '../../../CreditOperationAndSocialSecurityCards/components/ThemedInput';
import { Bank, findBankByCode, searchBanks } from './banks';
import * as S from './styles';

interface BankSelectProps {
    value: string;
    onChange: (code: string) => void;
    labelColor?: string;
}

const MAX_RESULTS = 30;

export const BankSelect = ({ value, onChange, labelColor = '#666' }: BankSelectProps) => {
    const [mode, setMode] = useState<'select' | 'manual'>(() =>
        (value && !findBankByCode(value)) ? 'manual' : 'select',
    );
    const [query, setQuery] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const selectedBank = findBankByCode(value);
    const results = searchBanks(query).slice(0, MAX_RESULTS);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelectBank = (bank: Bank) => {
        onChange(bank.code);
        setQuery('');
        setIsOpen(false);
    };

    const handleSelectOther = () => {
        onChange('');
        setQuery('');
        setIsOpen(false);
        setMode('manual');
    };

    const handleBackToList = () => {
        onChange('');
        setQuery('');
        setMode('select');
    };

    if (mode === 'manual') {
        return (
            <S.Wrapper>
                <S.ManualModeRow>
                    <S.Label $labelColor={labelColor}>Código do Banco</S.Label>
                    <S.SwitchModeLink type="button" onClick={handleBackToList}>
                        Buscar na lista
                    </S.SwitchModeLink>
                </S.ManualModeRow>
                <ThemedInput
                    labelValue=""
                    labelColor={labelColor}
                    fieldValue={value}
                    hasError={false}
                    errorMessage=""
                    type="numeric"
                    onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 3))}
                    onBlur={() => {}}
                />
            </S.Wrapper>
        );
    }

    const displayValue = isOpen ? query : selectedBank ? `${selectedBank.code} - ${selectedBank.name}` : '';

    return (
        <S.Wrapper ref={containerRef}>
            <S.Label $labelColor={labelColor}>Instituição financeira</S.Label>
            <S.InputWrapper>
                <S.Input
                    value={displayValue}
                    placeholder="Digite o nome do banco"
                    onFocus={() => {
                        setQuery('');
                        setIsOpen(true);
                    }}
                    onChange={(e) => {
                        setQuery(e.target.value);
                        setIsOpen(true);
                    }}
                />
                <S.ChevronIcon $open={isOpen}>
                    <ChevronDownIcon width={16} height={16} />
                </S.ChevronIcon>
            </S.InputWrapper>

            {isOpen && (
                <S.Dropdown>
                    {results.length === 0 && (
                        <S.DropdownEmpty>Nenhum banco encontrado</S.DropdownEmpty>
                    )}
                    {results.map((bank, index) => (
                        <S.DropdownItem
                            key={`${bank.code}-${index}`}
                            onClick={() => handleSelectBank(bank)}
                        >
                            <S.DropdownItemCode>{bank.code}</S.DropdownItemCode>
                            {bank.name}
                        </S.DropdownItem>
                    ))}
                    <S.OtherOption onClick={handleSelectOther}>
                        Outro - Digite o código
                    </S.OtherOption>
                </S.Dropdown>
            )}
        </S.Wrapper>
    );
};
